/* eslint-disable import/no-anonymous-default-export */

import moment from 'moment';
import * as R from 'ramda';
import { takeEvery, takeLatest } from 'redux-saga/effects';

const PENDING_SIGN_UP_EVENTS_KEY = 'tourconnect.userTracking.pendingSignUpEvents.v1';
const DELIVERED_SIGN_UP_EVENTS_KEY = 'tourconnect.userTracking.deliveredSignUpEvents.v1';
const DATA_LAYER_EVENT_TIMEOUT = 10e3;

export const SIGN_UP_EVENT_TYPES = [
  'bookingsAISignUp_doSignUp_SUCCESS',
  'generativeAiSignUp_doSignUp_SUCCESS',
];

export const NAMED_USER_EVENT_TYPES = [
  'bookingsAI_Upgrade_SUCCESS',
  'bookingsAISignUp_openMeetingSchedules',
  'generativeAiSignUp_openMeetingSchedules',
  'google_instructions_OpenGmail_SUCCESS',
  'google_instructions_OpenExtensionsStore_SUCCESS',
];

export const REPEATED_USER_EVENT_TYPES = [
  'generativeAI_uploadAsset_SUCCESS',
];

export const AFFILIATE_EVENT_TYPE = 'joinAffiliateProgram_SUCCESS';

let activeOwnerIds = [];
let trackingReady = false;
let memoryCommands = [];
let memoryCommandInFlight = false;
let persistentEventInFlight;

const reportTrackingDiagnostic = (stage, details = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'USER_TRACKING_DIAGNOSTIC',
    userTrackingDiagnostic: { stage, ...details },
  });
};

const readSessionArray = key => {
  try {
    const stored = window.sessionStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) return parsed;
    reportTrackingDiagnostic('storage_invalid', { key });
  } catch (error) {
    reportTrackingDiagnostic('storage_unavailable', { key });
  }
  return [];
};

const writeSessionArray = (key, value) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    reportTrackingDiagnostic('storage_unavailable', { key });
    return false;
  }
};

const hasValue = value => value !== undefined
  && value !== null
  && value !== ''
  && !(typeof value === 'number' && Number.isNaN(value));

const toUnixTimestamp = value => Math.floor(new Date(value).getTime() / 1000);

const pushTrackingCommand = (command, onComplete) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'TRACK_USER_EVENT',
    userTrackingEvent: {
      name: command.name,
      values: command.values || {},
    },
    eventCallback: onComplete,
    eventTimeout: DATA_LAYER_EVENT_TIMEOUT,
  });
};

const flushMemoryCommands = () => {
  if (!trackingReady || memoryCommandInFlight || !memoryCommands.length) return;
  const command = memoryCommands[0];
  memoryCommandInFlight = true;
  let completed = false;
  pushTrackingCommand(command, () => {
    if (completed) return;
    completed = true;
    memoryCommands.shift();
    memoryCommandInFlight = false;
    reportTrackingDiagnostic('command_delivered', {
      method: command.method,
      eventName: command.name || '',
    });
    flushTrackingCommands();
  });
};

const completePersistentEvent = event => {
  if (persistentEventInFlight !== event.id) return;
  const pending = readSessionArray(PENDING_SIGN_UP_EVENTS_KEY)
    .filter(item => item.id !== event.id);
  const delivered = readSessionArray(DELIVERED_SIGN_UP_EVENTS_KEY);
  if (!delivered.some(item => item.id === event.id)) {
    delivered.push({ id: event.id, deliveredAt: Date.now() });
  }
  writeSessionArray(PENDING_SIGN_UP_EVENTS_KEY, pending);
  writeSessionArray(DELIVERED_SIGN_UP_EVENTS_KEY, delivered.slice(-50));
  persistentEventInFlight = undefined;
  reportTrackingDiagnostic('command_delivered', {
    method: 'trackEvent',
    eventName: event.name,
  });
  flushTrackingCommands();
};

function flushPersistentSignUpEvents() {
  if (!trackingReady || persistentEventInFlight) return false;
  const pending = readSessionArray(PENDING_SIGN_UP_EVENTS_KEY);
  const deliveredIds = readSessionArray(DELIVERED_SIGN_UP_EVENTS_KEY)
    .map(event => event.id);
  const event = pending.find(item => deliveredIds.indexOf(item.id) === -1
    && item.ownerId
    && activeOwnerIds.indexOf(item.ownerId) > -1);

  pending
    .filter(item => item.ownerId && activeOwnerIds.indexOf(item.ownerId) === -1)
    .forEach(item => reportTrackingDiagnostic('event_owner_mismatch', {
      eventName: item.name,
    }));
  if (!event) return false;

  persistentEventInFlight = event.id;
  let completed = false;
  pushTrackingCommand({ method: 'trackEvent', name: event.name }, () => {
    if (completed) return;
    completed = true;
    completePersistentEvent(event);
  });
  return true;
}

const flushTrackingCommands = () => {
  if (!trackingReady) return;
  if (!flushPersistentSignUpEvents()) flushMemoryCommands();
};

const observeTrackingInstall = () => {
  trackingReady = true;
  flushTrackingCommands();
};

const queueMemoryCommand = command => {
  memoryCommands.push(command);
  reportTrackingDiagnostic('commands_queued', { count: memoryCommands.length });
  flushTrackingCommands();
};

const queuePersistentSignUpEvent = ({ type, payload = {} }) => {
  const ownerId = payload.userId || payload.companyId;
  const event = {
    id: `${type}:${ownerId || 'missing-owner'}`,
    name: type,
    ownerId: ownerId || null,
    queuedAt: Date.now(),
  };
  const pending = readSessionArray(PENDING_SIGN_UP_EVENTS_KEY);
  const delivered = readSessionArray(DELIVERED_SIGN_UP_EVENTS_KEY);
  if (pending.concat(delivered).some(item => item.id === event.id)) {
    reportTrackingDiagnostic('event_deduplicated', { eventName: type });
    return;
  }

  pending.push(event);
  writeSessionArray(PENDING_SIGN_UP_EVENTS_KEY, pending);
  reportTrackingDiagnostic('commands_queued', { count: pending.length });
  flushTrackingCommands();
};

export const clearUserTrackingSession = () => {
  activeOwnerIds = [];
  trackingReady = false;
  memoryCommands = [];
  memoryCommandInFlight = false;
  persistentEventInFlight = undefined;
  try {
    window.sessionStorage.removeItem(PENDING_SIGN_UP_EVENTS_KEY);
    window.sessionStorage.removeItem(DELIVERED_SIGN_UP_EVENTS_KEY);
  } catch (error) {
    reportTrackingDiagnostic('storage_unavailable', { key: 'user_tracking' });
  }
};

export function sendUserDataToTag({ payload }) {
  const {
    id: userId,
    user: {
      name,
      email,
      createdAt,
      intercomApp,
      intercomUserHash,
    },
    isMarketingReady,
    company: {
      companyId,
      companyName,
      companyType,
      plan,
      productType,
      websiteUrl,
      phone,
      acctCreationReason,
      createdAt: companyCreatedAt,
    },
    bookingSystem,
    emailClient,
    affiliateURL,
  } = payload;

  activeOwnerIds = [userId, companyId].filter(hasValue);
  trackingReady = false;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'GET_USER',
    user: {
      id: userId,
      name: name || '',
      email,
      createdAt: toUnixTimestamp(createdAt),
      app_id: intercomApp,
      user_hash: intercomUserHash,
    },
    company: {
      id: companyId,
      createdAt: toUnixTimestamp(companyCreatedAt),
      accountAge: moment().diff(companyCreatedAt, 'days'),
      creationReason: acctCreationReason,
      type: companyType,
      plan,
      name: companyName,
      website: websiteUrl,
      phone,
      isMarketingReady,
      productType,
      bookingSystem,
      emailClient,
      affiliateURL,
    },
    eventCallback: observeTrackingInstall,
    eventTimeout: DATA_LAYER_EVENT_TIMEOUT,
  });
}

export function trackEvent({ payload }) {
  if (payload.name) {
    queueMemoryCommand({
      method: 'trackEvent',
      name: payload.name,
      values: { ...payload.values },
    });
  }
  if (payload.ga) {
    const {
      ga: { eventCategory, eventValue, eventAction, eventLabel },
    } = payload;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'TRACK_EVENT',
      ev: {
        category: eventCategory,
        action: eventAction,
        label: eventLabel,
        value: eventValue,
      },
    });
  }
}

export const trackEventByType = ({ type }) => {
  queueMemoryCommand({ method: 'trackEvent', name: type });
};

export const sendAffiliateLinkToTracking = ({ payload }) => {
  const affiliateUrl = R.pathOr('', ['links', 0, 'url'], payload);
  queueMemoryCommand({
    method: 'trackEvent',
    name: AFFILIATE_EVENT_TYPE,
    values: { affiliate_url: affiliateUrl },
  });
};

export const trackSignUpEvent = action => {
  queuePersistentSignUpEvent(action);
};

export default [
  takeLatest('@app/GET_ACCOUNT_INFO_SUCCESS', sendUserDataToTag),
  takeEvery('TRACK_EVENT', trackEvent),
  takeLatest(AFFILIATE_EVENT_TYPE, sendAffiliateLinkToTracking),
  takeLatest(SIGN_UP_EVENT_TYPES, trackSignUpEvent),
  takeLatest(NAMED_USER_EVENT_TYPES, trackEventByType),
  takeEvery(REPEATED_USER_EVENT_TYPES, trackEventByType),
];
